import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { setPersonProperties } from "@/shared/utils/core";
import { useAppContext } from "./useAppContext";

const LAWYER_STATUS_KEY = "elayne_is_lawyer";

/**
 * Get cached lawyer status from localStorage (synchronous)
 */
export function getCachedLawyerStatus(): boolean {
  try {
    return localStorage.getItem(LAWYER_STATUS_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Set cached lawyer status in localStorage
 */
export function setCachedLawyerStatus(isLawyer: boolean): void {
  try {
    if (isLawyer) {
      localStorage.setItem(LAWYER_STATUS_KEY, "true");
    } else {
      localStorage.removeItem(LAWYER_STATUS_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Clear cached lawyer status from localStorage
 */
export function clearCachedLawyerStatus(): void {
  try {
    localStorage.removeItem(LAWYER_STATUS_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

export function useIsLawyer() {
  const { user, isLoading: userLoading } = useAppContext();

  // Get cached value for immediate use (prevents flicker)
  const cachedIsLawyer = getCachedLawyerStatus();

  const {
    data: isLawyer,
    isLoading: lawyerCheckLoading,
    isFetched,
  } = useQuery({
    queryKey: ["is-lawyer", user?.user_id],
    queryFn: async () => {
      if (!user?.user_id) {
        setCachedLawyerStatus(false);
        return false;
      }

      const { data, error } = await supabase
        .from("lawyers")
        .select("id, firm_name")
        .eq("user_id", user.user_id)
        .maybeSingle();

      if (error) {
        console.error("Error checking lawyer status:", error);
        return false;
      }

      const result = !!data;
      // Update cache with validated result
      setCachedLawyerStatus(result);
      setPersonProperties(
        data
          ? {
              user_type: "attorney",
              lawyer_user_id: user.user_id,
              ...(data.firm_name ? { firm_name: data.firm_name } : {}),
            }
          : { user_type: "family" },
      );
      return result;
    },
    enabled: !!user?.user_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Use cached value if we haven't fetched yet, otherwise use query result
  const effectiveIsLawyer = isFetched ? (isLawyer ?? false) : cachedIsLawyer;

  return {
    isLawyer: effectiveIsLawyer,
    // Only loading if user is loading AND we don't have a cached value
    // If we have a cached value, we can render immediately
    isLoading:
      userLoading || (!cachedIsLawyer && (lawyerCheckLoading || (!!user?.user_id && !isFetched))),
  };
}
